import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuLoaderCircle, LuShieldCheck } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { useTranslation } from '../../i18n/LanguageContext';

const DATA_ITEMS = ['identity', 'contact', 'employee', 'logs', 'biometric'];
const PURPOSE_ITEMS = ['contract', 'hr', 'security', 'legal', 'safety'];
const RIGHT_ITEMS = ['learn', 'info', 'purpose', 'thirdParties', 'correct', 'erase', 'notify', 'object', 'damages'];

const Section = ({ title, children }) => (
  <section className="tw-space-y-1.5">
    <h3 className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{title}</h3>
    {children}
  </section>
);

Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

const Paragraph = ({ children }) => (
  <p className="tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{children}</p>
);

Paragraph.propTypes = { children: PropTypes.node };

const List = ({ prefix, items }) => {
  const { t } = useTranslation();
  return (
    <ul className="tw-list-disc tw-space-y-1 tw-pl-5 tw-text-sm tw-leading-relaxed tw-text-slate-600 marker:tw-text-teal-500 dark:tw-text-slate-300">
      {items.map((item) => <li key={item} className="tw-list-item">{t(`${prefix}.${item}`)}</li>)}
    </ul>
  );
};

List.propTypes = { prefix: PropTypes.string.isRequired, items: PropTypes.arrayOf(PropTypes.string).isRequired };

/**
 * KVKK notice and explicit consent, asked once per employee at sign-in.
 * Closing it any other way than "Kabul Ediyorum" counts as not accepting.
 */
const KvkkModal = ({ isOpen, saving, onAccept, onReject }) => {
  const { t, language } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  const reject = () => {
    if (saving) return;
    setAccepted(false);
    onReject();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={reject}
      size="lg"
      title={t('login.kvkk.title')}
      subtitle={t('login.kvkk.subtitle')}
      closeLabel={t('login.kvkk.reject')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuShieldCheck aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={reject} disabled={saving}>{t('login.kvkk.reject')}</Button>
          <Button variant="primary" onClick={onAccept} disabled={!accepted || saving} className="max-sm:tw-flex-1">
            {saving && <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />}
            {t('login.kvkk.accept')}
          </Button>
        </>
      )}
    >
      <div className="tw-space-y-5">
        <Section title={t('login.kvkk.protectionTitle')}><Paragraph>{t('login.kvkk.protection')}</Paragraph></Section>
        <Section title={t('login.kvkk.controllerTitle')}><Paragraph>{t('login.kvkk.controller')}</Paragraph></Section>
        <Section title={t('login.kvkk.dataTitle')}><List prefix="login.kvkk.data" items={DATA_ITEMS} /></Section>
        <Section title={t('login.kvkk.purposeTitle')}><List prefix="login.kvkk.purpose" items={PURPOSE_ITEMS} /></Section>
        <Section title={t('login.kvkk.transferTitle')}><Paragraph>{t('login.kvkk.transfer')}</Paragraph></Section>
        <Section title={t('login.kvkk.rightsTitle')}>
          <Paragraph>{t('login.kvkk.rightsIntro')}</Paragraph>
          <List prefix="login.kvkk.rights" items={RIGHT_ITEMS} />
        </Section>

        <label
          htmlFor="kvkkConsent"
          className={`tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-p-4 tw-transition-colors ${
            accepted
              ? 'tw-border-teal-500/40 tw-bg-teal-500/10 dark:tw-border-teal-400/30'
              : 'tw-border-slate-200 tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'
          }`}
        >
          <input
            id="kvkkConsent"
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
            className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-cursor-pointer tw-accent-teal-600"
          />
          <span className="tw-text-sm tw-font-medium tw-leading-relaxed tw-text-slate-800 dark:tw-text-slate-100">{t('login.kvkk.consent')}</span>
        </label>
      </div>
    </Modal>
  );
};

KvkkModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  saving: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

export default KvkkModal;
